import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IClientPortalProposedWorksService } from "services/ClientPortalProposedWorksService";
import {
  ClientPortalProposedWorkDetailsModel,
  ClientPortalProposedWorkDocumentModel,
} from "services/api/cplapi/CPLAPI";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import { RootState } from "setup/redux/RootReducer";

type ProposedWorkDetailState = {
  ProposedWorkDetails: ClientPortalProposedWorkDetailsModel | null;
  Loading: boolean;
  LoadingDocuments: boolean;
  SubmittingVote: boolean;
  ProposedWorkDocuments: ClientPortalProposedWorkDocumentModel[] | null;
};

const initialState: ProposedWorkDetailState = {
  ProposedWorkDetails: null,
  Loading: false,
  LoadingDocuments: false,
  SubmittingVote: false,
  ProposedWorkDocuments: null,
};

export const ProposedWorksDetailSlice = createSlice({
  name: "ProposedWorksDetail",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getProposedWorksDetail.pending, (state) => {
        state.Loading = true;
      })
      .addCase(getProposedWorksDetail.fulfilled, (state, action) => {
        state.ProposedWorkDetails = action.payload;
        state.Loading = false;
      })
      .addCase(getProposedWorksDetail.rejected, (state) => {
        state.ProposedWorkDetails = new ClientPortalProposedWorkDetailsModel();
        state.Loading = false;
      })
      .addCase(voteOnProposedWork.pending, (state) => {
        state.SubmittingVote = true;
      })
      .addCase(voteOnProposedWork.fulfilled, (state) => {
        state.SubmittingVote = false;
      })
      .addCase(voteOnProposedWork.rejected, (state) => {
        state.SubmittingVote = false;
      })
      .addCase(getProposedWorkDocuments.pending, (state) => {
        state.Loading = true;
      })
      .addCase(getProposedWorkDocuments.fulfilled, (state, action) => {
        state.ProposedWorkDocuments = action.payload;
        state.Loading = false;
      })
      .addCase(getProposedWorkDocuments.rejected, (state) => {
        state.Loading = false;
      });
  },
});

export const getProposedWorksDetail = createAsyncThunk<
  ClientPortalProposedWorkDetailsModel,
  { proposedWorkID: string }
>("ProposedWorksDetail/GetProposedWorkDetails", async ({ proposedWorkID }) => {
  const proposedWorksService = container.get<IClientPortalProposedWorksService>(
    SERVICE_TYPES.ClientPortalProposedWorksService
  );
  return await proposedWorksService.getProposedWorksDetail(proposedWorkID);
});

export const voteOnProposedWork = createAsyncThunk<void, { proposedWorkID: string; agreed: boolean }>(
  "ProposedWorksDetail/VoteOnProposedWork",
  async ({ proposedWorkID, agreed }) => {
    const proposedWorksService = container.get<IClientPortalProposedWorksService>(
      SERVICE_TYPES.ClientPortalProposedWorksService
    );
    await proposedWorksService.voteOnProposedWork(proposedWorkID, agreed);
  }
);

export const getProposedWorkDocuments = createAsyncThunk<
  ClientPortalProposedWorkDocumentModel[],
  { proposedWorkID: string }
>("ProposedWorksDetail/GetProposedWorkDocuments", async ({ proposedWorkID }) => {
  const proposedWorksService = container.get<IClientPortalProposedWorksService>(
    SERVICE_TYPES.ClientPortalProposedWorksService
  );
  return await proposedWorksService.getProposedWorkDocuments(proposedWorkID);
});

export const selectProposedWorksDetail = (state: RootState) => state.proposedWorksDetail.ProposedWorkDetails;
export const selectLoadingProposedWorksDetail = (state: RootState) => state.proposedWorksDetail.Loading;
export const selectProposedWorksDocuments = (state: RootState) => state.proposedWorksDetail.ProposedWorkDocuments;
export const selectLoadingProposedWorksDocuments = (state: RootState) => state.proposedWorksDetail.LoadingDocuments;

export default ProposedWorksDetailSlice.reducer;
